import DOMPurify from 'dompurify'
import { escapeUTF8 } from 'entities'
import { compact as _compact, uniq as _uniq } from 'lodash-es'
import {
  cleanSpaceCharacters,
  isWhitespaceCharacter,
  normalizeTextForSearch,
  removeSpaceCharacters,
} from '@/utils/string.js'

/**
 * Text Highlight Directive
 *
 * Highlights matching text within elements based on a search query.
 * Supports case-sensitive/insensitive search, partial word matching, accent-sensitive/insensitive search, and custom CSS classes.
 *
 * @example
 * ```vue
 * <!-- Simple usage -->
 * <span v-text-highlight="searchQuery">{{ text }}</span>
 *
 * <!-- With options -->
 * <span v-text-highlight="{ query: 'hello', highlightClass: 'highlight', caseSensitive: true }">
 *   Hello World
 * </span>
 *
 * <!-- With fallback query -->
 * <span v-text-highlight="{ query: 'John', fallbackQuery: 'Doe', highlightClass: 'highlight' }">
 *   John Smith
 * </span>
 *
 * <!-- Case-sensitive via argument -->
 * <span v-text-highlight:case-sensitive="'hello'">Hello World</span>
 *
 * <!-- Accent-sensitive matching -->
 * <span v-text-highlight="{ query: 'cafe', accentSensitive: true }">Café</span>
 *
 * <!-- Accent-insensitive matching (default) -->
 * <span v-text-highlight="{ query: 'cafe' }">Café</span>
 *
 * <!-- Space-insensitive matching -->
 * <span v-text-highlight="{ query: 'Backgrand6', spaceSensitive: false }">Backgrand 6</span>
 * ```
 *
 * @security XSS-safe: Escapes HTML characters and sanitizes CSS class names
 */

export type TextHighlightOptions = {
  query: string
  highlightClass?: string
  caseSensitive?: boolean
  accentSensitive?: boolean
  spaceSensitive?: boolean
  fallbackQuery?: string
}

/**
 * Collapses all whitespace in the provided string for searching,
 * while keeping a mapping back to original indices so we can rebuild
 * the match spans with exact original spacing.
 */
function collapseWhitespaceAndMap(input: string): {
  collapsed: string
  indexMap: number[]
} {
  const collapsedPieces: string[] = []
  const indexMap: number[] = []

  for (let index = 0; index < input.length; index++) {
    const character = input[index]
    if (isWhitespaceCharacter(character)) {
      continue
    }

    collapsedPieces.push(character)
    indexMap.push(index)
  }

  return { collapsed: collapsedPieces.join(''), indexMap }
}

/**
 * Finds all matches of `searchToken` inside `subjectString`,
 * and maps them back to original text indices using `indexMap`.
 */
function findAllMatches(
  subjectString: string,
  searchToken: string,
  indexMap: number[],
  originalText: string,
): Array<{ start: number; end: number }> {
  const matches: Array<{ start: number; end: number }> = []

  if (searchToken.length === 0) {
    return matches
  }

  let currentIndex = subjectString.indexOf(searchToken, 0)
  while (currentIndex !== -1) {
    const startOriginalIndex = indexMap[currentIndex] ?? originalText.length
    const endOriginalIndex =
      (indexMap[currentIndex + searchToken.length - 1] ??
        originalText.length - 1) + 1

    matches.push({
      start: startOriginalIndex,
      end: endOriginalIndex,
    })

    currentIndex = subjectString.indexOf(searchToken, currentIndex + 1)
  }

  return matches
}

/**
 * Creates HTML with highlighted text based on search query.
 *
 * Processes the input text to find matches for the search query and wraps them in
 * <mark> tags with specified CSS classes. Supports partial word matching,
 * case-sensitive/insensitive search, and accent-sensitive/insensitive matching.
 *
 * @param text - The original text to search within
 * @param options - Configuration object containing query, highlight class, case sensitivity, and other options.
 * @returns HTML string with matching text wrapped in <mark> tags, all content is HTML-escaped for security
 *
 * @example
 * ```typescript
 * const result = createHighlightedHtml('Hello World', {
 *   query: 'hello',
 *   highlightClass: 'highlight'
 * });
 * // Returns: '<mark class="highlight">Hello</mark> World'
 *
 * // Accent-insensitive matching (default)
 * const result2 = createHighlightedHtml('Café', { query: 'cafe' });
 * // Returns: '<mark>Café</mark>' (original accents preserved in output)
 *
 * // Accent-sensitive matching
 * const result3 = createHighlightedHtml('Café', { query: 'cafe', accentSensitive: true });
 * // Returns: 'Café' (no match, no highlighting)
 * ```
 */
export function createHighlightedHtml(
  text: string,
  options: TextHighlightOptions,
) {
  const {
    query,
    highlightClass = '',
    caseSensitive = false,
    accentSensitive = false,
    spaceSensitive = true,
  } = options

  if (!query?.trim() || !text) {
    return text
  }

  // Normalization helpers
  const prepareCase = (text: string) =>
    caseSensitive ? text : text.toLowerCase()

  const prepareAccent = (text: string) =>
    accentSensitive ? text : normalizeTextForSearch(text)

  // Normalize the base text once
  const casePreparedText = prepareCase(text)
  const normalizedText = prepareAccent(casePreparedText)

  // Normalize and tokenize the query
  const preparedQuery = prepareAccent(prepareCase(query))
  const queryTokens = _uniq(_compact(preparedQuery.split(/\s+/))).sort(
    (tokenA, tokenB) => tokenB.length - tokenA.length,
  )

  // Build subject string and index map.
  const { collapsed: subjectString, indexMap } = spaceSensitive
    ? {
        collapsed: normalizedText,
        indexMap: [...normalizedText].map((_, index) => index),
      }
    : collapseWhitespaceAndMap(normalizedText)

  // Collect all matches, ignoring whitespace characters if spaceSensitive is false.
  const matchIntervals: { start: number; end: number }[] = []
  for (const queryToken of queryTokens) {
    const searchToken = spaceSensitive
      ? queryToken
      : removeSpaceCharacters(queryToken)
    matchIntervals.push(
      ...findAllMatches(subjectString, searchToken, indexMap, text),
    )
  }

  // If no matches, return the original text, with the whitespace characters
  // being converted to regular spaces.
  if (!matchIntervals?.length) {
    return escapeUTF8(cleanSpaceCharacters(text))
  }

  // Sort the matches by start and end indices and merge overlapping or adjacent intervals.
  // e.g. [1, 3] and [2, 4] should be merged to [1, 4].
  const mergedIntervals: { start: number; end: number }[] = []
  matchIntervals.sort(
    (matchA, matchB) => matchA.start - matchB.start || matchA.end - matchB.end,
  )
  for (const interval of matchIntervals) {
    const lastInterval = mergedIntervals[mergedIntervals.length - 1]

    if (!lastInterval || interval.start > lastInterval.end) {
      mergedIntervals.push({ ...interval })
    } else if (interval.end > lastInterval.end) {
      lastInterval.end = interval.end
    }
  }

  // Build HTML with escaped text and <mark> tags
  // e.g. if the original text is "Hello World Test" and the query is "Hello Test",
  // the mergedIntervals will be [[0, 5], [11, 16]] and the resultHtml will be
  // "<mark class="highlight">Hello</mark> World <mark class="highlight">Test</mark>".
  const safeHighlightClass = escapeUTF8(highlightClass)
  let resultHtml = ''
  let cursorIndex = 0
  for (const { start, end } of mergedIntervals) {
    if (cursorIndex < start) {
      resultHtml += escapeUTF8(text.slice(cursorIndex, start))
    }

    const highlightedSegment = escapeUTF8(text.slice(start, end))
    resultHtml += `<mark${
      safeHighlightClass ? ` class="${safeHighlightClass}"` : ''
    }>${highlightedSegment}</mark>`

    cursorIndex = end
  }

  if (cursorIndex < text.length) {
    resultHtml += escapeUTF8(text.slice(cursorIndex))
  }

  return spaceSensitive ? cleanSpaceCharacters(resultHtml) : resultHtml
}

/**
 * Main handler for setting text highlighting on element mount and updates.
 */
const handleTextHighlightDirective = (
  element: HTMLElement,
  binding: DirectiveBinding<string | TextHighlightOptions>,
) => {
  const originalText = element.dataset.originalText || element.textContent || ''

  // Store original text if not already stored
  if (!element.dataset.originalText) {
    element.dataset.originalText = originalText
  }

  const baseOptions: TextHighlightOptions = binding.value
    ? typeof binding.value === 'string'
      ? {
          query: binding.value,
          caseSensitive: binding.arg === 'case-sensitive',
          fallbackQuery: undefined,
        }
      : {
          ...binding.value,
          caseSensitive:
            binding.arg === 'case-sensitive' || binding.value.caseSensitive,
        }
    : { query: '', fallbackQuery: undefined }

  const highlightedHtml = createHighlightedHtml(originalText, baseOptions)
  let safeHighlightedHtml = DOMPurify.sanitize(highlightedHtml, {
    ALLOWED_TAGS: ['mark'],
    ALLOWED_ATTR: ['class'],
  })

  // If no matches found and fallback query is provided, retry with fallback
  if (!safeHighlightedHtml.includes('<mark') && baseOptions.fallbackQuery) {
    const fallbackOptions = { ...baseOptions, query: baseOptions.fallbackQuery }
    const fallbackHtml = createHighlightedHtml(originalText, fallbackOptions)
    const safeFallbackHtml = DOMPurify.sanitize(fallbackHtml, {
      ALLOWED_TAGS: ['mark'],
      ALLOWED_ATTR: ['class'],
    })

    // Use fallback result if it has matches, otherwise keep original
    if (safeFallbackHtml.includes('<mark')) {
      safeHighlightedHtml = safeFallbackHtml
    }
  }

  element.innerHTML = safeHighlightedHtml
}

/**
 * Vue directive object that handles text highlighting on element mount and updates.
 * Responds to changes in directive arguments, binding values, and element text content.
 */
const vTextHighlight = {
  mounted(
    element: HTMLElement,
    binding: DirectiveBinding<string | TextHighlightOptions>,
  ) {
    handleTextHighlightDirective(element, binding)
  },

  updated(
    element: HTMLElement,
    binding: DirectiveBinding<string | TextHighlightOptions>,
  ) {
    handleTextHighlightDirective(element, binding)
  },

  unmounted(element: HTMLElement) {
    // Clean up to prevent memory leaks
    // Remove all child nodes (text nodes, mark elements, etc.)
    while (element.firstChild) {
      element.removeChild(element.firstChild)
    }

    // Clear stored data
    delete element.dataset.originalText

    // Reset innerHTML to ensure all references are released
    element.innerHTML = ''
  },
}

export default vTextHighlight
